import React, { useState } from 'react';
import { View, TextInput, StyleSheet, Platform, ViewStyle } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Colors, Radii, ControlHeight, Fonts, Shadows } from '../constants';

interface SearchInputProps {
  value?: string;
  onChangeText?: (text: string) => void;
  onSearch?: (searchTerm: string) => void;
  placeholder?: string;
  /** 52px tall on the hero / filter panel, 48 elsewhere */
  size?: 'md' | 'lg';
  /** Hairline border — used on the linen hero and filter panel */
  bordered?: boolean;
  style?: ViewStyle;
  testID?: string;
}

/**
 * Search field (design v2): white, control radius (6), search icon on the left,
 * accent border + focus ring on focus. Borderless by default, hairline when
 * `bordered` (hero and filter panel).
 */
export const SearchInput: React.FC<SearchInputProps> = ({
  value: externalValue,
  onChangeText: externalOnChangeText,
  onSearch,
  placeholder = 'Search gear…',
  size = 'md',
  bordered = false,
  style,
  testID,
}) => {
  const [internalValue, setInternalValue] = useState('');
  const [focused, setFocused] = useState(false);

  // Use external value if provided (controlled), otherwise use internal state (uncontrolled)
  const searchTerm = externalValue !== undefined ? externalValue : internalValue;
  const setSearchTerm = externalOnChangeText || setInternalValue;
  const height = size === 'lg' ? ControlHeight.lg : ControlHeight.md;

  const handleSubmit = () => {
    onSearch?.(searchTerm.trim());
  };

  return (
    <View style={[styles.container, { height }, style]}>
      <Ionicons name="search-outline" size={20} color={Colors.textMedium} style={styles.icon} />
      <TextInput
        style={[
          styles.input,
          { height },
          { borderColor: focused ? Colors.primary : bordered ? Colors.borderLight : 'transparent' },
          focused && Platform.OS === 'web' && Shadows.focusRing,
          Platform.OS === 'web' && ({ outlineStyle: 'none' } as any),
        ]}
        value={searchTerm}
        onChangeText={setSearchTerm}
        placeholder={placeholder}
        placeholderTextColor={Colors.textLight}
        onSubmitEditing={handleSubmit}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        returnKeyType="search"
        autoCapitalize="none"
        autoCorrect={false}
        testID={testID}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    // `flex: 1` would set flex-basis 0 and collapse the field inside column
    // layouts (mobile hero / filter panel); grow-and-shrink from auto instead.
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 'auto',
    minWidth: 0,
    justifyContent: 'center',
  },
  icon: {
    position: 'absolute',
    left: 16,
    zIndex: 1,
  },
  input: {
    width: '100%',
    backgroundColor: Colors.white,
    borderWidth: 1,
    borderRadius: Radii.control,
    paddingLeft: 46,
    paddingRight: 16,
    fontFamily: Fonts.regular,
    fontSize: 15,
    color: Colors.text,
  },
});
